"use client";

import { useMemo, useState } from "react";
import { BOX_ATTR } from "@/lib/box";
import { cn } from "@/lib/format";
import type { Product } from "@/lib/shopify/types";
import { useCart } from "@/components/cart/CartProvider";
import { QuantityStepper } from "@/components/ui/QuantityStepper";
import { GiftNote } from "./GiftNote";
import { Price } from "./Price";

export function ProductForm({ product }: { product: Product }) {
  const { addLines, error } = useCart();
  const firstAvailable = product.variants.find((v) => v.availableForSale) ?? product.variants[0];
  const [selected, setSelected] = useState<Record<string, string>>(() =>
    Object.fromEntries((firstAvailable?.selectedOptions ?? []).map((o) => [o.name, o.value])),
  );
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "adding" | "failed">("idle");

  const variant = useMemo(
    () => product.variants.find((v) => v.selectedOptions.every((o) => selected[o.name] === o.value)),
    [product.variants, selected],
  );

  // Options worth showing: more than one value, and not Shopify's placeholder.
  const options = product.options.filter((o) => o.values.length > 1 || (o.name !== "Title" && o.values[0] !== "Default Title"));

  const isAvailable = (optionName: string, value: string) =>
    product.variants.some(
      (v) =>
        v.availableForSale &&
        v.selectedOptions.every((o) => (o.name === optionName ? o.value === value : selected[o.name] === o.value)),
    );

  const add = async () => {
    if (!variant) return;
    setStatus("adding");
    const note = message.trim();
    const ok = await addLines([
      {
        merchandiseId: variant.id,
        quantity,
        attributes: note ? [{ key: BOX_ATTR.message, value: note }] : [],
      },
    ]);
    setStatus(ok ? "idle" : "failed");
    if (ok) {
      setQuantity(1);
      setMessage("");
    }
  };

  const soldOut = !variant?.availableForSale;

  return (
    <div>
      <p className="text-2xl">
        <Price
          price={variant?.price ?? product.priceRange.min}
          compareAtPrice={variant ? variant.compareAtPrice : product.compareAtPrice}
        />
      </p>

      {options.map((option) => (
        <fieldset key={option.id} className="mt-7">
          <legend className="text-[0.9375rem] text-paper">
            {option.name}: <span className="text-paper">{selected[option.name]}</span>
          </legend>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {option.values.map((value) => {
              const active = selected[option.name] === value;
              const available = isAvailable(option.name, value);
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setSelected((current) => ({ ...current, [option.name]: value }))}
                  className={cn(
                    "min-h-11 rounded-[4px] border px-4 text-[0.9375rem]",
                    active ? "border-gold bg-gold text-on-gold" : "border-paper/40 hover:border-paper",
                    !available && !active && "text-paper line-through",
                  )}
                >
                  {value}
                  {available ? null : <span className="sr-only"> (sold out)</span>}
                </button>
              );
            })}
          </div>
        </fieldset>
      ))}

      <GiftNote value={message} onChange={setMessage} />

      <div className="mt-7 flex gap-3">
        <QuantityStepper quantity={quantity} onChange={setQuantity} min={1} label={product.title} disabled={soldOut} />
        <button type="button" className="btn btn-primary min-h-10 flex-1" onClick={add} disabled={soldOut || status === "adding"}>
          {soldOut ? "Sold out" : status === "adding" ? "Adding to cart" : "Add to cart"}
        </button>
      </div>
      {status === "failed" && error ? (
        <p role="alert" className="mt-3 text-[0.9375rem] text-gold">
          {error}
        </p>
      ) : null}
    </div>
  );
}
